// API Endpoints
export const STORE_ENDPOINTS = {
  GET_ALL_STORES: '/v1/stores/',
  GET_STORE_BY_ID: (storeId: string) => `/v1/stores/${storeId}`,
  GET_POS_CREDENTIALS_SUMMARY: (storeId: string) => `/v1/stores/${storeId}/pos-credentials-summary`,
  GET_STORE_LOCATIONS: (storeId: string) => `/v1/stores/${storeId}/locations`,
  CREATE_STORE: '/v1/stores/',
  UPDATE_STORE: (storeId: string) => `/v1/stores/${storeId}`,
  DELETE_STORE: (storeId: string) => `/v1/stores/${storeId}`,
  UPLOAD_LOGO: (storeId: string) => `/v1/stores/${storeId}/logo`,
  GET_LOGO: (logoUrl: string) => logoUrl,
  INVITE_USER: (storeId: string) => `/v1/stores/${storeId}/users`,
  UPDATE_USER: (storeId: string, userId: string) => `/v1/stores/${storeId}/users/${userId}`,
  DELETE_USER: (storeId: string, userId: string) => `/v1/stores/${storeId}/users/${userId}`,
  GET_CLOVER_STATUS: (storeId: string) => `/v1/stores/${storeId}/integrations/clover/status`,
  CONNECT_CLOVER: (storeId: string) => `/v1/stores/${storeId}/integrations/clover/connect`,
  SYNC_CLOVER: (storeId: string) => `/v1/stores/${storeId}/integrations/clover/sync`,
  DISCONNECT_CLOVER: (storeId: string) => `/v1/stores/${storeId}/integrations/clover`,
  GET_STORE_PASSWORDS: (storeId: string) => `/v1/store-passwords/${storeId}`,
  REVEAL_STORE_PASSWORD: (storeId: string, service: string) => `/v1/store-passwords/admin/${storeId}/${encodeURIComponent(service)}/password`,
} as const;

// Validation Messages
export const STORE_VALIDATION_MESSAGES = {
  NAME_REQUIRED: 'Store name is required',
  NAME_MIN_LENGTH: 'Store name must be at least 2 characters',
  NAME_MAX_LENGTH: 'Store name must be less than 100 characters',
  ADDRESS_REQUIRED: 'Store address is required',
  CITY_REQUIRED: 'City is required',
  STATE_REQUIRED: 'State is required',
  ZIP_CODE_REQUIRED: 'ZIP code is required',
  ZIP_CODE_INVALID: 'ZIP code must be 5 digits',
  PHONE_REQUIRED: 'Phone number is required',
  PHONE_INVALID: 'Phone number must be 10 digits',
  EMAIL_REQUIRED: 'Email is required',
  EMAIL_INVALID: 'Please enter a valid email address',
  LOGO_SIZE_LIMIT: 'Logo file size must be less than 5MB',
  LOGO_TYPE_INVALID: 'Logo must be an image file (JPG, PNG, GIF)',
  USER_NAME_REQUIRED: 'User name is required',
  USER_EMAIL_REQUIRED: 'User email is required',
  USER_EMAIL_INVALID: 'Please enter a valid email address',
  USER_PHONE_REQUIRED: 'User phone number is required',
  USER_PHONE_INVALID: 'Phone number must be 10 digits',
  USER_ROLE_REQUIRED: 'User role is required',
} as const;

// Store Configuration
export const STORE_CONFIG = {
  LOGO_MAX_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_LOGO_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'],
  PHONE_REGEX: /^[0-9]{10}$/,
  ZIP_CODE_REGEX: /^[0-9]{5}$/,
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
} as const;

// Store Status
export const STORE_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const;

// Subscription Status
export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  PENDING_ACTIVATION: 'pending_activation',
  PENDING_CANCEL: 'pending_cancel',
  TRIAL: 'trial',
  CANCELED: 'canceled',
} as const;

// User Roles
export const USER_ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  EMPLOYEE: 'employee',
} as const;

// User Status
export const USER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const;

// Local Storage Keys
export const STORE_STORAGE_KEYS = {
  SELECTED_STORE: 'selectedStore',
  STORE_FILTERS: 'storeFilters',
  STORE_SORT: 'storeSort',
} as const;

// Error Messages
export const STORE_ERROR_MESSAGES = {
  FETCH_STORES_FAILED: 'Failed to fetch stores',
  FETCH_STORE_FAILED: 'Failed to fetch store details',
  CREATE_STORE_FAILED: 'Failed to create store',
  UPDATE_STORE_FAILED: 'Failed to update store',
  DELETE_STORE_FAILED: 'Failed to delete store',
  UPLOAD_LOGO_FAILED: 'Failed to upload logo',
  INVITE_USER_FAILED: 'Failed to invite user',
  UPDATE_USER_FAILED: 'Failed to update user',
  DELETE_USER_FAILED: 'Failed to delete user',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  SERVER_ERROR: 'Server error. Please try again later.',
  UNKNOWN_ERROR: 'An unexpected error occurred.',
} as const;

// Success Messages
export const STORE_SUCCESS_MESSAGES = {
  STORE_CREATED: 'Store created successfully',
  STORE_UPDATED: 'Store updated successfully',
  STORE_DELETED: 'Store deleted successfully',
  LOGO_UPLOADED: 'Logo uploaded successfully',
  USER_INVITED: 'User invited successfully',
  USER_UPDATED: 'User updated successfully',
  USER_DELETED: 'User deleted successfully',
} as const;

// Form Field Names
export const STORE_FORM_FIELDS = {
  NAME: 'name',
  ADDRESS: 'address',
  CITY: 'city',
  STATE: 'state',
  ZIP_CODE: 'zip_code',
  PHONE_NUMBER: 'phone_number',
  EMAIL: 'email',
  LOGO: 'logo',
} as const;

// User Form Field Names
export const USER_FORM_FIELDS = {
  NAME: 'name',
  EMAIL: 'email',
  PHONE_NUMBER: 'phone_number',
  ROLE: 'role',
  STATUS: 'status',
} as const;

// Tab Configuration
export * from './tabs';
